<template>
    <Tab
        v-bind:value="activeTabValue"
        tabStyle="tab-common main"
        v-bind:tabList="formattedTabItems"
        v-on:update:value="onTabValueUpdate"
    />
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { computed, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import Tab from '@/components/common/Tab.vue';
    import type { IPopoverItem, ITabItem } from '@/interface/Tab';
    import { DisplaySection, MarketPage, RoutePage, SportType } from '@/interface/enum';
    import { useZone } from '@/libraries/composables/useZone';
    import { isOlympicsZone } from '@/libraries/helpers/multipleSportsZoneHelper';
    import { getSportTypeKey } from '@/libraries/helpers/sportsHelper';
    import { i18n } from '@/libraries/i18n';
    import { useRootStore } from '@/stores/rootStore';

    const { routePage, displaySection } = defineProps<{
        routePage: RoutePage;
        displaySection?: DisplaySection;
    }>();

    const gaPrefix = computed(() => {
        if (displaySection === DisplaySection.TournamentCenter_Matches) {
            return 'TC_MultipleZone';
        }
        if (routePage === RoutePage.SimulationSportsZone) {
            return 'CyberSports';
        }
        return 'MultipleZone';
    });
    const { sportType, marketPage, isMixParlayMode } = storeToRefs(useRootStore());
    const { sportList, showOutright, isInSimulationSportsZone } = useZone();

    function getTitle(sport: SportType) {
        if (isInSimulationSportsZone.value) {
            return i18n.t(`simulation_sport_type_name_${sport}`);
        }

        if (isOlympicsZone() && sport === SportType.Olympic) {
            return i18n.t('multipleSports_others');
        }

        return i18n.t(`sportType_${getSportTypeKey(sport)}`);
    }

    const outsideTabCount = computed(() => (isInSimulationSportsZone.value ? 3 : 2));
    const outsideSportList = computed(() => sportList.value.slice(0, outsideTabCount.value));
    const otherSportList = computed(() => sportList.value.slice(outsideTabCount.value));

    function createTabValue(targetMarketPage: MarketPage, targetSportType: SportType) {
        return `${targetMarketPage}:${targetSportType}`;
    }

    const activeTabValue = computed(() => createTabValue(
        marketPage.value === MarketPage.Outright ? MarketPage.Outright : MarketPage.All,
        sportType.value,
    ));

    const outsideSportTabs = computed((): ITabItem[] => outsideSportList.value
        .map(x => ({
            value: createTabValue(MarketPage.All, x),
            title: getTitle(x),
            tabGA: `${gaPrefix.value}_Tab_${getSportTypeKey(x)}`,
        })));

    const moreSportTabs = computed((): IPopoverItem[] => otherSportList.value
        .map(x => ({
            value: createTabValue(MarketPage.All, x),
            title: getTitle(x),
            shortTitle: null,
            tabGA: `${gaPrefix.value}_Tab_More_${getSportTypeKey(x)}`,
            count: null,
            disabled: false,
        })));

    const outrightSportTabs = computed((): IPopoverItem[] => sportList.value
        .map(x => ({
            value: createTabValue(MarketPage.Outright, x),
            title: getTitle(x),
            shortTitle: null,
            tabGA: `${gaPrefix.value}_Tab_Outright_${getSportTypeKey(x)}`,
            count: null,
            disabled: false,
        })));

    const isOutrightActive = computed(() => outrightSportTabs.value.some(x => x.value === activeTabValue.value) && marketPage.value === MarketPage.Outright);
    const formattedTabItems = computed((): ITabItem[] => [
        ...outsideSportTabs.value,
        {
            value: createTabValue(MarketPage.All, SportType.Unknown),
            title: i18n.t('multipleSports_more'),
            tabGA: `${gaPrefix.value}_Tab_More`,
            popoverList: moreSportTabs.value,
        },
        ...(!isMixParlayMode.value && showOutright.value ? [{
            value: createTabValue(MarketPage.Outright, SportType.Unknown),
            title: i18n.t('marketType_OutRight'),
            tabGA: `${gaPrefix.value}_Tab_Outright`,
            popoverList: outrightSportTabs.value,
        }] : []),
    ]);

    const router = useRouter();
    function onTabValueUpdate(value: string | number) {
        if (typeof value !== 'string') return;

        const [newMarketPage, newSportType] = value.split(':');
        onUpdate(Number.parseInt(newSportType) as SportType, newMarketPage as MarketPage);
    }

    function onUpdate(newSportType: SportType, newMarketPage: MarketPage) {
        if (!newSportType) return;
        if (newSportType === sportType.value && newMarketPage === marketPage.value) return;

        const params: Record<string, string> = {
            sportRouteName: SportType.toRouteName(newSportType),
        };

        if (newMarketPage === MarketPage.Outright) {
            params.outright = 'outright';
        }

        router.push({
            name: routePage,
            params,
        });
    }

    watch([isMixParlayMode, isOutrightActive], ([isMixParlayMode], [_, isOutrightActiveBefore]) => {
        if (isMixParlayMode && isOutrightActiveBefore) {
            onUpdate(SportType.Soccer, MarketPage.All);
        }
    });

</script>

<style lang="scss">
    @import "@/scss/components/tab";
</style>
